import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { playSoftAlert } from "@/lib/sound";
import { Bell, Car, Footprints, Bike, Check, Clock, ChevronDown, LogOut, Loader2, XCircle, History, X } from "lucide-react";

const MODE_ICON = { auto: Car, pie: Footprints, moto: Bike };
const MODE_LABEL = { auto: "En auto", pie: "A pie", moto: "En moto" };
const STATUS_BADGE = {
  recibido: { label: "Solicitada", cls: "bg-navy/10 text-navy" },
  preparando: { label: "Preparando", cls: "bg-orange-100 text-orange-700" },
  entregado: { label: "Entregada", cls: "bg-emerald-100 text-emerald-700" },
  cancelado: { label: "Cancelada por el tutor", cls: "bg-red-100 text-red-700" },
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function PickupAlerts() {
  const { user } = useAuth();
  const enabled = user && (user.role === "member" || user.role === "staff" || user.role === "owner");
  const [pickups, setPickups] = useState([]);
  const [cancelList, setCancelList] = useState([]);
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const seenRef = useRef(new Set());
  const seenCancelRef = useRef(new Set());
  const dismissedRef = useRef(new Set());
  const firstRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;
    let on = true;
    const load = async () => {
      try {
        const { data } = await api.get("/pickups");
        if (!on) return;
        const list = data.pickups || [];
        const cancels = data.cancellations || [];
        const ids = new Set(list.map((p) => p.id));
        const hasNew = list.some((p) => !seenRef.current.has(p.id));
        if (hasNew && !firstRef.current) {
          try { playSoftAlert(); } catch {}
          try { navigator.vibrate?.(200); } catch {}
          const nuevo = list.find((p) => !seenRef.current.has(p.id));
          if (nuevo) toast.info(`Salida programada: ${nuevo.student_name}`);
          setOpen(true);
        }
        // Cancelaciones hechas por el tutor: avisar y mostrar chip en la fila (aunque desaparezca de pendientes)
        const fresh = [];
        for (const c of cancels) {
          if (dismissedRef.current.has(c.id)) continue;
          fresh.push(c);
          if (!seenCancelRef.current.has(c.id)) {
            seenCancelRef.current.add(c.id);
            if (!firstRef.current) {
              try { playSoftAlert(); } catch {}
              try { navigator.vibrate?.(120); } catch {}
              toast.warning(`El papá canceló la salida de ${c.student_name} (${c.grade})`, { duration: 8000 });
              setOpen(true);
            }
          }
        }
        setCancelList(fresh);
        seenRef.current = ids;
        firstRef.current = false;
        setPickups(list);
      } catch { /* silencio */ }
    };
    load();
    const t = setInterval(load, 7000);
    return () => { on = false; clearInterval(t); };
  }, [enabled]);

  // Recordatorio suave: sigue sonando cada 18s mientras haya salidas sin aceptar (estado "recibido").
  // NO reabre el panel: respeta el minimizado del usuario (solo suena y el globo pulsante avisa).
  const pending = pickups.some((p) => p.status === "recibido");
  useEffect(() => {
    if (!enabled || !pending) return;
    const t = setInterval(() => {
      try { playSoftAlert(); } catch {}
    }, 18000);
    return () => clearInterval(t);
  }, [enabled, pending]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/pickups/${id}/status`, { status });
      if (status === "entregado") {
        setPickups((prev) => prev.filter((p) => p.id !== id));
        toast.success("Alumno entregado. El papá fue notificado.");
      } else {
        setPickups((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      }
    } catch { toast.error("No se pudo actualizar"); }
    finally { setBusyId(null); }
  };

  const dismissCancel = (id) => {
    dismissedRef.current.add(id);
    setCancelList((prev) => prev.filter((c) => c.id !== id));
  };

  const openHistory = async () => {
    setHistOpen(true);
    setHistLoading(true);
    try {
      const { data } = await api.get("/pickups/history");
      setHist(data.items || []);
    } catch { toast.error("No se pudo cargar el historial"); }
    finally { setHistLoading(false); }
  };

  if (!enabled) return null;

  const hasItems = pickups.length > 0 || cancelList.length > 0;

  return (
    <>
      {hasItems && open && (
        <div className="fixed bottom-20 left-4 md:bottom-4 z-[60] w-[92vw] max-w-sm" data-testid="pickup-alerts">
          <div className="bg-white rounded-2xl border border-navy/20 shadow-2xl shadow-navy/25 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-navy text-white">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                </span>
                <LogOut className="w-4 h-4" /> Salidas · {pickups.length}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={openHistory} data-testid="pickup-history-btn" className="text-white/80 hover:text-white p-1" title="Historial del día">
                  <History className="w-4 h-4" />
                </button>
                <button onClick={() => setOpen(false)} data-testid="pickup-alerts-min" className="text-white/80 hover:text-white p-1" title="Minimizar">
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-nardo-100">
              {cancelList.map((c) => (
                <div key={c.id} className="p-3.5 bg-red-50/60 flex items-start gap-3" data-testid={`pickup-cancel-${c.id}`}>
                  <div className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center bg-red-100 text-red-600">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-navy text-sm truncate">{c.student_name}</div>
                    <div className="text-xs text-nardo-500">{[c.grade && c.grade !== "—" ? c.grade : null, c.level].filter(Boolean).join(" · ")}{c.cancelled_at ? ` · ${fmtTime(c.cancelled_at)}` : ""}</div>
                    <span className="inline-block mt-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Cancelada por el tutor</span>
                  </div>
                  <button onClick={() => dismissCancel(c.id)} data-testid={`pickup-cancel-dismiss-${c.id}`} className="text-nardo-400 hover:text-navy p-1" title="Descartar">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {pickups.map((p) => {
                const Icon = MODE_ICON[p.mode] || Car;
                const prep = p.status === "preparando";
                return (
                  <div key={p.id} className="p-3.5" data-testid={`pickup-item-${p.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-xl shrink-0 flex items-center justify-center font-display font-bold text-lg ${prep ? "bg-orange-100 text-orange-600" : "bg-navy/10 text-navy"}`}>
                        {p.student_name?.[0] || "A"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-navy text-sm truncate">{p.student_name}</div>
                        <div className="text-xs text-nardo-500">{[p.grade && p.grade !== "—" ? p.grade : null, p.level].filter(Boolean).join(" · ")}{p.tutor_name ? ` · ${p.tutor_name}` : ""}</div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-nardo-600 mt-1.5">
                          <span className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5 text-navy" /> {MODE_LABEL[p.mode]}</span>
                          {p.eta && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-navy" /> {p.eta}</span>}
                          {p.plates && <span className="font-mono">{p.plates}</span>}
                          {p.car_color && <span>{p.car_color}</span>}
                        </div>
                        {prep && <div className="text-[11px] text-orange-600 font-medium mt-1">Preparando salida…</div>}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {!prep && (
                        <button onClick={() => setStatus(p.id, "preparando")} disabled={busyId === p.id} data-testid={`pickup-prep-${p.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-orange-300 text-orange-700 hover:bg-orange-50 rounded-lg py-2 disabled:opacity-50 transition-colors">
                          <Clock className="w-3.5 h-3.5" /> Preparando
                        </button>
                      )}
                      <button onClick={() => setStatus(p.id, "entregado")} disabled={busyId === p.id} data-testid={`pickup-deliver-${p.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2 disabled:opacity-50 transition-colors">
                        {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Ya se entregó
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {hasItems && !open && (
        <button onClick={() => setOpen(true)} data-testid="pickup-alerts-badge"
          className="fixed bottom-20 left-4 md:bottom-4 z-[60] inline-flex items-center gap-2 bg-navy text-white rounded-full pl-3 pr-4 py-2.5 shadow-xl shadow-navy/30 hover:bg-navy-ink transition-colors animate-pulse">
          <Bell className="w-5 h-5" />
          <span className="text-sm font-semibold">Salidas · {pickups.length + cancelList.length}</span>
        </button>
      )}

      {!hasItems && (
        <button onClick={openHistory} data-testid="pickup-history-idle" title="Historial de salidas"
          className="fixed right-4 bottom-[214px] md:bottom-[150px] z-[58] w-12 h-12 flex items-center justify-center rounded-full bg-white text-navy border border-navy/20 shadow-lg hover:bg-navy hover:text-white transition-colors">
          <History className="w-5 h-5" />
        </button>
      )}

      {histOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" data-testid="pickup-history-modal" onClick={() => setHistOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 bg-navy text-white">
              <div className="flex items-center gap-2 font-semibold text-sm"><History className="w-4 h-4" /> Historial de salidas · hoy</div>
              <button onClick={() => setHistOpen(false)} data-testid="pickup-history-close" className="text-white/80 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto divide-y divide-nardo-100">
              {histLoading ? (
                <div className="py-12 flex justify-center text-navy"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : hist.length === 0 ? (
                <div className="py-12 text-center text-sm text-nardo-500">Sin solicitudes de salida hoy.</div>
              ) : hist.map((h) => {
                const b = STATUS_BADGE[h.status] || STATUS_BADGE.recibido;
                const t = h.cancelled_at || h.delivered_at || h.created_at;
                return (
                  <div key={h.id} className="px-5 py-3 flex items-center gap-3" data-testid={`pickup-hist-${h.id}`}>
                    <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center bg-navy/10 text-navy font-display font-bold">{h.student_name?.[0] || "A"}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-navy text-sm truncate">{h.student_name} <span className="text-xs font-normal text-nardo-500">· {[h.grade && h.grade !== "—" ? h.grade : null, h.level].filter(Boolean).join(" · ") || h.grade}</span></div>
                      <div className="text-xs text-nardo-500">{MODE_LABEL[h.mode] || "—"}{t ? ` · ${fmtTime(t)}` : ""}{h.delivered_by ? ` · por ${h.delivered_by}` : ""}</div>
                    </div>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${b.cls}`}>{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
